/*
 * Product card and grid presentation.
 *
 * 1. Size strip arrows — the strip (.hdt-card-size-bar--slider) is a horizontal
 *    scroller rendered by snippets/product-card-sizes.liquid whenever the
 *    available sizes are too wide to fit the card. This adds click-to-scroll
 *    arrows and keeps their state in sync with the scroll position.
 * 2. Overlay stack height — publishes the measured height of .hdt-card-overlays
 *    as --hdt-overlay-stack-h so the quick-add button can clear sizes plus the
 *    offer badge, whatever the badge ends up containing.
 * 3. Trailing row — hides an incomplete last row while more products can still
 *    be loaded, so the grid never ends on a lone card. See fillGrid below.
 *
 * Everything is delegated from the document so cards added later — filtering,
 * pagination, infinite scroll, quick view — work without re-initialising.
 */
(() => {
  const TRACK = '.hdt-card-size-track';
  const NAV = '.hdt-card-size-nav';
  const STACK = '.hdt-card-overlays';
  const GRID = 'hdt-reval-items.hdt-row-grid';
  const CARDS = ':scope > .hdt-card-product, :scope > .hdt-banner_product-card';
  const REMAINDER = 'hdt-card--row-remainder';

  /* How far one arrow click moves the strip: most of the visible width, with a
     little overlap so the size you were looking at stays partly in view. */
  const stepFor = (track) => Math.max(60, track.clientWidth * 0.75);

  const syncArrows = (track) => {
    const bar = track.closest('.hdt-card-size-bar');
    if (!bar) return;
    /* Sub-pixel widths mean scrollLeft rarely hits the exact maximum. */
    const max = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= max - 1;
    bar.classList.toggle('is-at-start', atStart);
    bar.classList.toggle('is-at-end', atEnd || max <= 0);
  };

  document.addEventListener('click', (event) => {
    const nav = event.target.closest(NAV);
    if (!nav) return;
    /* The strip sits on top of the product image link. */
    event.preventDefault();
    event.stopPropagation();

    const bar = nav.closest('.hdt-card-size-bar');
    const track = bar && bar.querySelector(TRACK);
    if (!track) return;

    const dir = nav.dataset.dir === 'prev' ? -1 : 1;
    track.scrollBy({ left: dir * stepFor(track), behavior: 'smooth' });
  });

  /* scroll does not bubble, but it can still be caught in the capture phase,
     which keeps this to a single listener for the whole page. */
  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (track instanceof Element && track.matches(TRACK)) syncArrows(track);
    },
    true
  );

  const syncStackHeight = (stack) => {
    const media = stack.parentElement;
    if (!media) return;
    media.style.setProperty('--hdt-overlay-stack-h', `${Math.round(stack.offsetHeight)}px`);
  };

  /*
   * Shopify counts products with no image toward each batch of 24, but the theme
   * does not render them — so a batch can produce 23 or 18 cards and leave the
   * last row short. While there are still products to load, hide those trailing
   * cards; the next "Load more" brings them back as part of a complete row. On
   * the final batch nothing is hidden, so no product is ever unreachable.
   */
  const hasMoreToLoad = () => Boolean(document.querySelector('[loadmore-btn-wrapp] a'));

  const columnCount = (grid) => {
    const cols = getComputedStyle(grid).gridTemplateColumns;
    if (!cols || cols === 'none') return 1;
    return cols.split(' ').filter(Boolean).length;
  };

  const fillGrid = (grid) => {
    const cards = Array.from(grid.querySelectorAll(CARDS));
    /* Count against the full set, not the currently visible one. */
    cards.forEach((card) => card.classList.remove(REMAINDER));
    if (!cards.length || !hasMoreToLoad()) return;

    const remainder = cards.length % columnCount(grid);
    if (!remainder) return;
    cards.slice(cards.length - remainder).forEach((card) => card.classList.add(REMAINDER));
  };

  let queued = false;
  const refresh = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      document.querySelectorAll(GRID).forEach(fillGrid);
    });
  };

  /* Both measurements depend on the rendered card width, so measure rather than
     guess — card widths change with breakpoint, sidebar filters and zoom. */
  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const el = entry.target;
      if (el.matches(TRACK)) syncArrows(el);
      else if (el.matches(STACK)) syncStackHeight(el);
      /* Column count changes at breakpoints, which changes the remainder. */
      else fillGrid(el);
    }
  });

  const init = (root) => {
    root.querySelectorAll(TRACK).forEach((track) => {
      if (track.dataset.sizeNavReady) return;
      track.dataset.sizeNavReady = '1';
      observer.observe(track);
      syncArrows(track);
    });
    root.querySelectorAll(STACK).forEach((stack) => {
      if (stack.dataset.stackReady) return;
      stack.dataset.stackReady = '1';
      observer.observe(stack);
      syncStackHeight(stack);
    });
    document.querySelectorAll(GRID).forEach((grid) => {
      if (grid.dataset.gridFillReady) return;
      grid.dataset.gridFillReady = '1';
      observer.observe(grid);
    });
    refresh();
  };

  const start = () => init(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* Cards injected after load (filters, pagination, quick view, recommendations). */
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches(TRACK) || node.matches(STACK)) {
          init(node.parentElement || document);
        } else if (node.querySelector && node.querySelector(`${TRACK}, ${STACK}`)) {
          init(node);
        }
      }
    }
    /* Also covers the Load more button disappearing on the final batch, which
       is what releases the last hidden cards. */
    refresh();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
